import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { DownloadIcon } from 'shared/components/icons/DownloadIcon';
import { FileContract } from '@sighten/beacon/icons';
import {
  AgreementCardContent,
  AgreementInfoRow,
  AgreementLabel,
  AgreementValue,
  NoticeText,
  DownloadButton,
} from './AgreementCard.styles';
import { Card } from '../../../../shared/components/Card/Card';
import { useDownloadContract } from 'api/agreement';
import { toast } from 'shared/components/Toast/Toast';

type Props = {
  agreementSigned: string;
  agreementTerm: number;
  agreementId: string;
  showNotice?: boolean;
  hideDetails?: boolean;
};

export function AgreementCard({
  agreementSigned,
  agreementTerm,
  agreementId,
  showNotice,
  hideDetails,
}: Props) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'support&FAQs.faqCategories.agreementTransfer.sidebar',
  });

  const theme = useTheme();

  const { mutate: downloadContract } = useDownloadContract({
    onSuccess: (data) => {
      const { presigned_url: presignedUrl } = data;
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = 'signed_everbright_contract.pdf';
      link.click();
      link.remove();
    },
    onError: () => {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('projectQualification.somethingWentWrong'),
        theme,
      });
    },
  });

  const formattedAgreementSigned = new Date(agreementSigned).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Card
      title={t('agreement')}
      icon={{
        element: <FileContract size="lg" />,
        color: 'grey',
      }}
      width="100%"
    >
      <AgreementCardContent>
        {!hideDetails && (
          <>
            <AgreementInfoRow>
              <AgreementLabel>{t('agreementSigned')}</AgreementLabel>
              <AgreementValue>{formattedAgreementSigned}</AgreementValue>
            </AgreementInfoRow>
            <AgreementInfoRow>
              <AgreementLabel>{t('agreementTerm')}</AgreementLabel>
              <AgreementValue>
                {agreementTerm} {t('years')}
              </AgreementValue>
            </AgreementInfoRow>
          </>
        )}
        {showNotice && <NoticeText>{t('transferNotice')}</NoticeText>}
        <DownloadButton onClick={() => downloadContract(agreementId)}>
          {t('download')}
          <DownloadIcon />
        </DownloadButton>
      </AgreementCardContent>
    </Card>
  );
}

export default AgreementCard;
